import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { humanise } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, useToast } from '../components/ui';

interface LocationNode {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  path: string;
  kind: string;
  pincode: string | null;
  lat: number | null;
  lng: number | null;
}

const CHILD_KIND: Record<string, string> = {
  COUNTRY: 'STATE',
  STATE: 'DISTRICT',
  DISTRICT: 'CITY',
  CITY: 'AREA',
};

/**
 * v2 §8/§33 — India → State → District → City/Area.
 *
 * Coordinates are not decoration: `nearest` resolves a device's position
 * through them, and provider radius search falls back to an area's centre
 * when the client sends only a pincode. A place with no coordinates is
 * invisible to both.
 */
export function LocationsPage() {
  const [stack, setStack] = useState<LocationNode[]>([]);
  const [editing, setEditing] = useState<{ parent: LocationNode | null; row?: LocationNode } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const parent = stack[stack.length - 1] ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ['locations', parent?.id ?? 'root'],
    queryFn: () => api.get<LocationNode[]>(parent ? `/locations?parentId=${parent.id}` : '/locations'),
  });

  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id ? api.patch(`/locations/${id}`, body) : api.post('/locations', body),
    onSuccess: () => {
      toast('Location saved', 'success');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['locations'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const nextKind = parent ? CHILD_KIND[parent.kind] : 'COUNTRY';

  return (
    <div className="stack">
      <Notice tone="info" title="Coordinates matter">
        Device-location detection and the 20 km provider search both resolve through these coordinates. A place without
        them cannot be matched to a user standing in it.
      </Notice>

      <div className="row row--wrap">
        <button className="btn btn--ghost btn--sm" onClick={() => setStack([])}>
          India
        </button>
        {stack.map((node, i) => (
          <span key={node.id} className="row" style={{ gap: 6 }}>
            <span className="cell-muted">/</span>
            <button className="btn btn--ghost btn--sm" onClick={() => setStack(stack.slice(0, i + 1))}>
              {node.name}
            </button>
          </span>
        ))}
        <div className="spacer" />
        {nextKind && (
          <Button variant="primary" size="sm" onClick={() => setEditing({ parent })}>
            + New {humanise(nextKind).toLowerCase()}
          </Button>
        )}
      </div>

      <Card flush>
        <CardHead title={parent ? `Inside ${parent.name}` : 'Countries'} />
        {isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 140 }} />
          </div>
        ) : !data?.length ? (
          <Empty icon="⌘" title="Nothing here yet" body={nextKind ? `Add a ${humanise(nextKind).toLowerCase()} to get started.` : undefined} />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Pincode</th>
                  <th>Coordinates</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((node) => (
                  <tr key={node.id}>
                    <td className="cell-strong">{node.name}</td>
                    <td>
                      <Badge tone="neutral">{humanise(node.kind)}</Badge>
                    </td>
                    <td className="cell-mono">{node.pincode ?? '—'}</td>
                    <td className="cell-muted">
                      {node.lat != null && node.lng != null ? (
                        `${node.lat.toFixed(4)}, ${node.lng.toFixed(4)}`
                      ) : (
                        <Badge tone="amber">not set</Badge>
                      )}
                    </td>
                    <td>
                      <div className="cell-actions">
                        {CHILD_KIND[node.kind] && (
                          <Button size="sm" onClick={() => setStack([...stack, node])}>
                            Open →
                          </Button>
                        )}
                        <Button size="sm" onClick={() => setEditing({ parent, row: node })}>
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <LocationEditor
          editing={editing}
          kind={editing.row?.kind ?? nextKind}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(body) => save.mutate({ id: editing.row?.id, body })}
        />
      )}
    </div>
  );
}

function LocationEditor({
  editing,
  kind,
  onClose,
  onSave,
  saving,
}: {
  editing: { parent: LocationNode | null; row?: LocationNode };
  kind: string;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const row = editing.row;
  const [form, setForm] = useState({
    name: row?.name ?? '',
    pincode: row?.pincode ?? '',
    lat: row?.lat != null ? String(row.lat) : '',
    lng: row?.lng != null ? String(row.lng) : '',
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  function submit() {
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      pincode: form.pincode.trim() || undefined,
      lat: form.lat ? Number(form.lat) : undefined,
      lng: form.lng ? Number(form.lng) : undefined,
    };
    if (!row) {
      body.kind = kind;
      if (editing.parent) body.parentId = editing.parent.id;
    }
    onSave(body);
  }

  return (
    <Modal
      title={row ? `Edit ${row.name}` : `New ${humanise(kind).toLowerCase()}`}
      sub={editing.parent && !row ? `Inside ${editing.parent.name}` : undefined}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!form.name.trim()} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <Input value={form.name} autoFocus onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Pincode" hint="Six digits. Used when a search sends only a pincode.">
          <Input value={form.pincode} onChange={(e) => set('pincode', e.target.value)} placeholder="500081" />
        </Field>
        <div className="row">
          <Field label="Latitude">
            <Input type="number" step="0.0001" value={form.lat} onChange={(e) => set('lat', e.target.value)} placeholder="17.4483" />
          </Field>
          <Field label="Longitude">
            <Input type="number" step="0.0001" value={form.lng} onChange={(e) => set('lng', e.target.value)} placeholder="78.3915" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
